import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { authApi } from "@/api/auth";
import { Alert, Button, Card, PasswordInput } from "@/components/common";
import { ApiError } from "@/types/api";

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const { token, email } = useSearch({ strict: false }) as {
    token?: string;
    email?: string;
  };

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const [errors, setErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});

  function validate(): boolean {
    const next: { password?: string; confirmPassword?: string } = {};

    if (!password) {
      next.password = "Hasło jest wymagane";
    } else if (password.length < 12) {
      next.password = "Hasło musi mieć co najmniej 12 znaków";
    } else if (!/[A-Z]/.test(password)) {
      next.password = "Hasło musi zawierać co najmniej jedną wielką literę";
    } else if (!/[a-z]/.test(password)) {
      next.password = "Hasło musi zawierać co najmniej jedną małą literę";
    } else if (!/[0-9]/.test(password)) {
      next.password = "Hasło musi zawierać co najmniej jedną cyfrę";
    } else if (!/[^a-zA-Z0-9]/.test(password)) {
      next.password = "Hasło musi zawierać co najmniej jeden znak specjalny";
    }

    if (!confirmPassword) {
      next.confirmPassword = "Potwierdzenie hasła jest wymagane";
    } else if (password !== confirmPassword) {
      next.confirmPassword = "Hasła nie są identyczne";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit() {
    setServerError("");

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await authApi.resetPassword(email ?? "", token ?? "", password);
      setIsSuccess(true);
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 400) {
        setServerError(
          "Link do resetowania hasła wygasł lub jest nieprawidłowy.",
        );
      } else {
        setServerError(
          err instanceof ApiError
            ? err.message
            : "Wystąpił błąd. Spróbuj ponownie później.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!token || !email) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-subtle px-4">
        <Card padding="lg" className="w-full max-w-md text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="mb-2 text-xl font-semibold text-main">
            Nieprawidłowy link
          </h1>
          <p className="mb-6 text-sm text-dim">
            Link do resetowania hasła jest nieprawidłowy lub niekompletny.
            Spróbuj ponownie wysłać prośbę o reset hasła.
          </p>
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-adara-700 transition-colors hover:text-adara-500"
          >
            Resetuj hasło ponownie
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      {/* Left branding panel */}
      <div className="relative hidden overflow-hidden bg-linear-to-br from-adara-700 via-adara-800 to-adara-950 lg:flex lg:w-[45%] lg:flex-col lg:items-center lg:justify-center">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-panel/5" />
        <div className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-panel/5" />
        <div className="absolute top-1/4 right-1/4 h-40 w-40 rounded-full bg-gold-400/10" />

        <div className="relative z-10 flex flex-col items-center px-12">
          <img
            src="/logos/adara-logo-white.svg"
            alt="Adara"
            className="mb-8 w-72"
          />
          <div className="mb-6 h-0.5 w-16 bg-linear-to-r from-gold-300 to-gold-500" />
          <p className="text-center text-sm tracking-widest text-white/70 uppercase">
            Panel Agenta
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex flex-1 flex-col items-center justify-center bg-subtle px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-8 lg:hidden">
          <img src="/logos/adara-logo.svg" alt="Adara" className="h-12" />
        </div>

        <Card padding="lg" className="w-full max-w-md animate-fade-in">
          {isSuccess ? (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <svg
                  className="h-6 w-6 text-green-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>

              <h1 className="mb-2 text-2xl font-semibold text-main">
                Hasło zmienione
              </h1>
              <p className="mb-6 text-sm text-dim">
                Twoje hasło zostało pomyślnie zmienione. Możesz się teraz
                zalogować.
              </p>

              <Button
                fullWidth
                onClick={() => {
                  void navigate({
                    to: "/login",
                    search: { redirect: undefined },
                  });
                }}
              >
                Przejdź do logowania
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h1 className="text-2xl font-semibold text-main">
                  Ustaw nowe hasło
                </h1>
                <p className="mt-1.5 text-sm text-dim">
                  Wprowadź nowe hasło do swojego konta.
                </p>
              </div>

              {serverError && (
                <div className="mb-6">
                  <Alert
                    variant="error"
                    onDismiss={() => {
                      setServerError("");
                    }}
                  >
                    {serverError}
                  </Alert>
                </div>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleSubmit();
                }}
                aria-label="Formularz ustawiania nowego hasła"
                noValidate
              >
                <div className="flex flex-col gap-5">
                  <PasswordInput
                    label="Nowe hasło"
                    autoComplete="new-password"
                    placeholder="Min. 12 znaków"
                    value={password}
                    error={errors.password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password) {
                        setErrors((prev) => ({
                          ...prev,
                          password: undefined,
                        }));
                      }
                    }}
                  />

                  <PasswordInput
                    label="Potwierdź hasło"
                    autoComplete="new-password"
                    placeholder="Powtórz nowe hasło"
                    value={confirmPassword}
                    error={errors.confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errors.confirmPassword) {
                        setErrors((prev) => ({
                          ...prev,
                          confirmPassword: undefined,
                        }));
                      }
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    size="lg"
                    isLoading={isSubmitting}
                  >
                    Zmień hasło
                  </Button>
                </div>
              </form>
            </>
          )}
        </Card>

        <p className="mt-8 text-center text-xs text-faint">
          &copy; {new Date().getFullYear()} Adara. Wszelkie prawa zastrzeżone.
        </p>
      </div>
    </div>
  );
}
